import React, { useState, useEffect } from "react";
import ReactTable from "react-table-v6";
import Loader from "../../../../../../settings/loader";
import commaNumber from "comma-number";
import { exportPdf } from "../../../../../../exportPdf";
import "react-table-v6/react-table.css";
import "./style.scss";

const Slider4 = (props) => {
  const [data, setData] = useState({
    result: null,
    table: [],
    brandwiseData: [],
  });
  const [load, setLoad] = useState(0);
  const [activeBrand, setActiveBrand] = useState(0);

  // Define all utility functions first
  const topParser = (tableData) => {
    if (!tableData || tableData.length === 0) {
      return { max: 0, ave: 0, min: 0 };
    }

    let qtyArray = tableData.map((item) => item.qty);

    return {
      max: Math.max(...qtyArray),
      ave: qtyArray[Math.floor((qtyArray.length - 1) / 2)],
      min: Math.min(...qtyArray),
    };
  };

  const sortSales = (a, b) => {
    return parseInt(b.qty) - parseInt(a.qty);
  };

  const _initialized = () => {
    if (props.data && !load) {
      setData((prevData) => ({
        ...prevData,
        result: {
          ...props.filter.data,
          ...props.data,
        },
      }));
      setLoad(1);
    }
  };

  const loadData = () => {
    if (load === 1 && data.result) {
      let items = [];

      data.result.sales.data.forEach((dataItem) => {
        const brand = props.data.brands.data.find(
          (brand) => brand.braKey === dataItem.braKey
        );
        const chain = props.data.chains.data.find(
          (chain) => chain.chaKey === dataItem.chaKey
        );

        if (!brand || !chain) return;

        dataItem.items.forEach((item) => {
          // Find existing item with same refNo, name, brandKey, and chaKey
          const queryItem = items.find(
            (storedItem) =>
              storedItem.refNo === item.refNo &&
              storedItem.name === item.name &&
              storedItem.brandKey === brand.braKey &&
              storedItem.chaKey === chain.chaKey
          );

          let qty = item.qty || 1;
          const itemPrice = item.amount; // Original price before discount
          const discountAmount = item.discount ? item.discount.discountAmt : 0;
          const finalPrice = itemPrice - discountAmount;
          const totalAmount = finalPrice * qty;

          const discountQty =
            item.discount && item.discount.discountAmt > 0 ? qty : 0;
          const totalDiscountAmount = discountAmount * qty;

          const revCent = dataItem.revCent || "Unknown Revenue Center";

          if (!queryItem) {
            // Create new item entry
            items.push({
              ...item,
              qty: qty,
              amount: itemPrice, // Store original price
              discountQty: discountQty,
              total: totalAmount,
              totalDiscountAmount: totalDiscountAmount,
              brandKey: brand.braKey,
              brandName: brand.name,
              chaKey: chain.chaKey,
              chainName: chain.name,
              businessName: [
                {
                  name: `${chain.name} - ${brand.name}`,
                  amount: totalAmount,
                  qty: qty,
                  currency: chain?.currency?.toUpperCase(),
                },
              ],
              // Store price variants for items with same refNo but different prices
              priceVariants: [
                {
                  price: itemPrice,
                  discount: item.discount,
                  qty: qty,
                  total: totalAmount,
                  totalDiscountAmount: totalDiscountAmount,
                  revCenterBreakdown: [
                    {
                      revCenterName: revCent,
                      qty: qty,
                      amount: totalAmount,
                      currency: chain?.currency?.toUpperCase() || "QAR",
                    },
                  ],
                },
              ],
              revCenterBreakdown: [
                {
                  revCenterName: revCent,
                  qty: qty,
                  amount: totalAmount,
                  currency: chain?.currency?.toUpperCase() || "QAR",
                },
              ],
              originalDiscount: item.discount,
              aggregatedDiscount: item.discount
                ? {
                    discountAmt: item.discount.discountAmt,
                    totalDiscountAmount: totalDiscountAmount,
                  }
                : null,
            });
          } else {
            // Check if this is a different price variant
            const existingPriceVariant = queryItem.priceVariants.find(
              (variant) => variant.price === itemPrice
            );

            if (existingPriceVariant) {
              // Same price variant - aggregate
              existingPriceVariant.qty += qty;
              existingPriceVariant.total += totalAmount;
              existingPriceVariant.totalDiscountAmount += totalDiscountAmount;

              // Update revenue center breakdown for this price variant
              const existingRevCenter =
                existingPriceVariant.revCenterBreakdown.find(
                  (rc) =>
                    rc.revCenterName.toLowerCase().trim() ===
                    revCent.toLowerCase().trim()
                );
              if (existingRevCenter) {
                existingRevCenter.qty += qty;
                existingRevCenter.amount += totalAmount;
              } else {
                existingPriceVariant.revCenterBreakdown.push({
                  revCenterName: revCent,
                  qty: qty,
                  amount: totalAmount,
                  currency: chain?.currency?.toUpperCase() || "QAR",
                });
              }
            } else {
              // Different price variant - add as new variant
              queryItem.priceVariants.push({
                price: itemPrice,
                discount: item.discount,
                qty: qty,
                total: totalAmount,
                totalDiscountAmount: totalDiscountAmount,
                revCenterBreakdown: [
                  {
                    revCenterName: revCent,
                    qty: qty,
                    amount: totalAmount,
                    currency: chain?.currency?.toUpperCase() || "QAR",
                  },
                ],
              });
            }

            // Update main item aggregated values
            queryItem.qty += qty;
            queryItem.discountQty += discountQty;
            queryItem.total += totalAmount;
            queryItem.totalDiscountAmount += totalDiscountAmount;

            // Update main item business names
            queryItem.businessName[0].amount += totalAmount;
            queryItem.businessName[0].qty += qty;

            // Update main item revenue center breakdown
            const existingMainRevCenter = queryItem.revCenterBreakdown.find(
              (rc) =>
                rc.revCenterName.toLowerCase().trim() ===
                revCent.toLowerCase().trim()
            );
            if (existingMainRevCenter) {
              existingMainRevCenter.qty += qty;
              existingMainRevCenter.amount += totalAmount;
            } else {
              queryItem.revCenterBreakdown.push({
                revCenterName: revCent,
                qty: qty,
                amount: totalAmount,
                currency: chain?.currency?.toUpperCase() || "QAR",
              });
            }

            // Update aggregated discount
            if (totalDiscountAmount > 0) {
              if (!queryItem.aggregatedDiscount) {
                queryItem.aggregatedDiscount = {
                  discountAmt: 0,
                  totalDiscountAmount: 0,
                };
              }
              queryItem.aggregatedDiscount.totalDiscountAmount +=
                totalDiscountAmount;
              queryItem.aggregatedDiscount.discountAmt =
                queryItem.aggregatedDiscount.totalDiscountAmount /
                queryItem.qty;
            }
          }
        });
      });

      // Sort items by outlet name first, then by quantity
      const sortedItems = items.sort((a, b) => {
        // First sort by chain name (outlet)
        const chainComparison = a.chainName.localeCompare(b.chainName);
        if (chainComparison !== 0) {
          return chainComparison;
        }
        // Then sort by quantity (descending)
        return parseInt(b.qty) - parseInt(a.qty);
      });

      const filteredItems = sortedItems.filter((item) => item);

      // Group items by brand
      const brandwiseData = props.data.brands.data
        .map((brand) => ({
          brandName: brand.name,
          brandKey: brand.braKey,
          items: filteredItems.filter((item) => item.brandKey === brand.braKey),
        }))
        .filter((brand) => brand.items.length > 0);

      setData((prevData) => ({
        ...prevData,
        table: filteredItems,
        brandwiseData: brandwiseData,
      }));
      setLoad(2);
    }
  };

  useEffect(() => {
    if (load === 0) {
      _initialized();
    } else if (load === 1) {
      loadData();
    }
  }, [load, props.data, props.filter?.data]);

  useEffect(() => {
    if (data.brandwiseData.length === 1 && activeBrand === 0) {
      setActiveBrand(1);
    } else if (data.brandwiseData.length === 1 && activeBrand !== 1) {
      setActiveBrand(1);
    }
  }, [data.brandwiseData.length, activeBrand]);

  // SINGLE EXPORT useEffect
  useEffect(() => {
    if (props.export && props.getData && data.table.length > 0) {
      if (props.exportType === "pdf") {
        exportToPdf();
      } else if (props.exportType === "excel-multisheet") {
        exportAllBrandData();
      } else {
        exportCurrentBrandData();
      }
    }
  }, [
    props.export,
    data.table,
    props.filter?.data,
    props.getData,
    activeBrand,
    data.brandwiseData,
    props.exportType,
  ]);

  const exportToPdf = () => {
    const currentData = getCurrentTableData();
    const currentBrandName = getCurrentBrandName();

    // Format data for PDF export - flatten price variants
    const flattenedData = [];
    currentData.forEach((item) => {
      if (item.priceVariants && item.priceVariants.length > 1) {
        // Main row
        flattenedData.push([
          String(item.businessName[0]?.name || "Unknown Business"),
          String(item.refNo),
          String(item.name),
          "", // Empty for main row
          String(item.businessName[0]?.currency || "QAR"),
          "", // Empty for main row
          "", // Empty for main row
          commaNumber(item.total.toFixed(2)),
          item.revCenterBreakdown
            .map((revCenter) => {
              return `${revCenter.revCenterName} | Qty ${
                revCenter.qty
              } | ${commaNumber(revCenter.amount.toFixed(2))} ${
                revCenter.currency
              }`;
            })
            .join(" | "),
        ]);

        // Sub-rows for each price variant
        item.priceVariants.forEach((variant) => {
          flattenedData.push([
            "", // Empty for sub-rows
            "", // Empty for sub-rows
            "", // Empty for sub-rows
            String(variant.qty),
            String(variant.revCenterBreakdown[0]?.currency || "QAR"),
            commaNumber(variant.price.toFixed(2)),
            variant.totalDiscountAmount > 0
              ? commaNumber(variant.totalDiscountAmount.toFixed(2))
              : "0",
            commaNumber(variant.total.toFixed(2)),
            variant.revCenterBreakdown
              .map((revCenter) => {
                return `${revCenter.revCenterName} | Qty ${
                  revCenter.qty
                } | ${commaNumber(revCenter.amount.toFixed(2))} ${
                  revCenter.currency
                }`;
              })
              .join(" | "),
          ]);
        });
      } else {
        // Single price item
        const variant = item.priceVariants?.[0] || item;
        flattenedData.push([
          String(item.businessName[0]?.name || "Unknown Business"),
          String(item.refNo),
          String(item.name),
          String(item.qty),
          String(item.businessName[0]?.currency || "QAR"),
          commaNumber((variant.price || item.amount).toFixed(2)),
          item.totalDiscountAmount > 0
            ? commaNumber(item.totalDiscountAmount.toFixed(2))
            : "0",
          commaNumber(item.total.toFixed(2)),
          item.revCenterBreakdown
            .map((revCenter) => {
              return `${revCenter.revCenterName} | Qty ${
                revCenter.qty
              } | ${commaNumber(revCenter.amount.toFixed(2))} ${
                revCenter.currency
              }`;
            })
            .join(" | "),
        ]);
      }
    });

    const pdfData = {
      type: "table",
      title: `Sales by Revenue Center Items - ${currentBrandName}`,
      filter: getFilterData(),
      body: [
        {
          head: [
            [
              "Business Name",
              "Item No.",
              "Name",
              "Qty",
              "Currency",
              "Price",
              "Discount",
              "Sales",
              "Revenue Centers",
            ],
          ],
          body: flattenedData,
        },
      ],
    };

    exportPdf.table(pdfData);
  };

  // Helper function for filter data
  const getFilterData = () => {
    if (data.brandwiseData.length === 1) {
      const selectedBrand = data.brandwiseData[0];
      return {
        brands: [selectedBrand.brandName],
        chains: props.filter.data.chains.map((chain) => chain.name),
        revCent: props.filter.data.revCent.map((revCent) => revCent.name),
        payType: props.filter.data.payType.map((payType) => payType.name),
        date: {
          start: props.filter.data.date.start,
          end: props.filter.data.date.end,
        },
      };
    } else if (activeBrand === 0) {
      return {
        brands: props.filter.data.brands.map((brand) => brand.name),
        chains: props.filter.data.chains.map((chain) => chain.name),
        revCent: props.filter.data.revCent.map((revCent) => revCent.name),
        payType: props.filter.data.payType.map((payType) => payType.name),
        date: {
          start: props.filter.data.date.start,
          end: props.filter.data.date.end,
        },
      };
    } else {
      const selectedBrand = data.brandwiseData[activeBrand - 1];
      return {
        brands: [selectedBrand.brandName],
        chains: props.filter.data.chains.map((chain) => chain.name),
        revCent: props.filter.data.revCent.map((revCent) => revCent.name),
        payType: props.filter.data.payType.map((payType) => payType.name),
        date: {
          start: props.filter.data.date.start,
          end: props.filter.data.date.end,
        },
      };
    }
  };

  const exportCurrentBrandData = () => {
    const currentData = getCurrentTableData();
    const currentBrandName = getCurrentBrandName();

    const flattenedExportData = [];
    currentData.forEach((item) => {
      if (item.priceVariants && item.priceVariants.length > 1) {
        // Main row for Excel
        flattenedExportData.push([
          String(item.businessName[0]?.name || "Unknown Business"),
          String(item.refNo),
          String(item.name),
          "", // Empty for main row
          String(item.businessName[0]?.currency || "QAR"),
          "", // Empty for main row
          "", // Empty for main row
          parseFloat(item.total.toFixed(2)),
          String(
            item.revCenterBreakdown
              .map((revCenter) => {
                return `${revCenter.revCenterName} | Qty ${
                  revCenter.qty
                } | ${commaNumber(revCenter.amount.toFixed(2))} ${
                  revCenter.currency
                }`;
              })
              .join("\n")
          ),
        ]);

        // Sub-rows for each price variant
        item.priceVariants.forEach((variant) => {
          flattenedExportData.push([
            "", // Empty for sub-rows
            "", // Empty for sub-rows
            "", // Empty for sub-rows
            parseFloat(variant.qty),
            String(variant.revCenterBreakdown[0]?.currency || "QAR"),
            parseFloat(variant.price.toFixed(2)),
            parseFloat(variant.totalDiscountAmount || 0),
            parseFloat(variant.total.toFixed(2)),
            String(
              variant.revCenterBreakdown
                .map((revCenter) => {
                  return `${revCenter.revCenterName} | Qty ${
                    revCenter.qty
                  } | ${commaNumber(revCenter.amount.toFixed(2))} ${
                    revCenter.currency
                  }`;
                })
                .join("\n")
            ),
          ]);
        });
      } else {
        // Single price item
        const variant = item.priceVariants?.[0] || item;
        flattenedExportData.push([
          String(item.businessName[0]?.name || "Unknown Business"),
          String(item.refNo),
          String(item.name),
          parseFloat(item.qty),
          String(item.businessName[0]?.currency || "QAR"),
          parseFloat((variant.price || item.amount).toFixed(2)),
          parseFloat(item.totalDiscountAmount || 0),
          parseFloat(item.total.toFixed(2)),
          String(
            item.revCenterBreakdown
              .map((revCenter) => {
                return `${revCenter.revCenterName} | Qty ${
                  revCenter.qty
                } | ${commaNumber(revCenter.amount.toFixed(2))} ${
                  revCenter.currency
                }`;
              })
              .join("\n")
          ),
        ]);
      }
    });

    props.getData({
      type: "table",
      isMultiSheet: false,
      sheetName: `${currentBrandName}`,
      title: `Sales by Revenue Center Items - ${currentBrandName}`,
      filter: getFilterData(),
      body: [
        {
          head: [
            [
              "Business Name",
              "Item No.",
              "Name",
              "Qty",
              "Currency",
              "Price",
              "Discount",
              "Sales",
              "Revenue Centers",
            ],
          ],
          columnTypes: [
            "text",
            "text",
            "text",
            "number",
            "text",
            "number",
            "number",
            "number",
            "text",
          ],
          body: flattenedExportData,
        },
      ],
      excelOptions: {
        columnWidths: [
          { width: 25 },
          { width: 15 },
          { width: 30 },
          { width: 10 },
          { width: 10 },
          { width: 15 },
          { width: 15 },
          { width: 15 },
          { width: 40 },
        ],
        numberFormats: {
          3: "0",
          5: "0.00",
          6: "0.00",
          7: "0.00",
        },
        wrapText: [8],
      },
    });
  };

  const exportAllBrandData = () => {
    const sheets = data.brandwiseData.map((brand) => {
      const flattenedData = [];
      brand.items.forEach((item) => {
        if (item.priceVariants && item.priceVariants.length > 1) {
          // Main row
          flattenedData.push([
            String(item.businessName[0]?.name || "Unknown Business"),
            String(item.refNo),
            String(item.name),
            "", // Empty for main row
            String(item.businessName[0]?.currency || "QAR"),
            "", // Empty for main row
            "", // Empty for main row
            parseFloat(item.total.toFixed(2)),
            String(
              item.revCenterBreakdown
                .map((revCenter) => {
                  return `${revCenter.revCenterName} | Qty ${
                    revCenter.qty
                  } | ${commaNumber(revCenter.amount.toFixed(2))} ${
                    revCenter.currency
                  }`;
                })
                .join("\n")
            ),
          ]);

          // Sub-rows
          item.priceVariants.forEach((variant) => {
            flattenedData.push([
              "", // Empty for sub-rows
              "", // Empty for sub-rows
              "", // Empty for sub-rows
              parseFloat(variant.qty),
              String(variant.revCenterBreakdown[0]?.currency || "QAR"),
              parseFloat(variant.price.toFixed(2)),
              parseFloat(variant.totalDiscountAmount || 0),
              parseFloat(variant.total.toFixed(2)),
              String(
                variant.revCenterBreakdown
                  .map((revCenter) => {
                    return `${revCenter.revCenterName} | Qty ${
                      revCenter.qty
                    } | ${commaNumber(revCenter.amount.toFixed(2))} ${
                      revCenter.currency
                    }`;
                  })
                  .join("\n")
              ),
            ]);
          });
        } else {
          const variant = item.priceVariants?.[0] || item;
          flattenedData.push([
            String(item.businessName[0]?.name || "Unknown Business"),
            String(item.refNo),
            String(item.name),
            parseFloat(item.qty),
            String(item.businessName[0]?.currency || "QAR"),
            parseFloat((variant.price || item.amount).toFixed(2)),
            parseFloat(item.totalDiscountAmount || 0),
            parseFloat(item.total.toFixed(2)),
            String(
              item.revCenterBreakdown
                .map((revCenter) => {
                  return `${revCenter.revCenterName} | Qty ${
                    revCenter.qty
                  } | ${commaNumber(revCenter.amount.toFixed(2))} ${
                    revCenter.currency
                  }`;
                })
                .join("\n")
            ),
          ]);
        }
      });

      return {
        sheetName: brand.brandName.replace(/[^\w\s]/gi, "").substring(0, 31),
        head: [
          [
            "Business Name",
            "Item No.",
            "Name",
            "Qty",
            "Currency",
            "Price",
            "Discount",
            "Sales",
            "Revenue Centers",
          ],
        ],
        columnTypes: [
          "text",
          "text",
          "text",
          "number",
          "text",
          "number",
          "number",
          "number",
          "text",
        ],
        body: flattenedData,
        excelOptions: {
          columnWidths: [
            { width: 25 },
            { width: 15 },
            { width: 30 },
            { width: 10 },
            { width: 10 },
            { width: 15 },
            { width: 15 },
            { width: 15 },
            { width: 40 },
          ],
          numberFormats: {
            3: "0",
            5: "0.00",
            6: "0.00",
            7: "0.00",
          },
          wrapText: [8],
        },
      };
    });

    if (data.brandwiseData.length > 1) {
      const summarySheet = {
        sheetName: "Summary",
        head: [["Brand Name", "Total Items", "Total Quantity", "Total Sales"]],
        columnTypes: ["text", "number", "number", "number"],
        body: data.brandwiseData.map((brand) => {
          const totalQty = brand.items.reduce((sum, item) => sum + item.qty, 0);
          const totalSales = brand.items.reduce(
            (sum, item) => sum + item.total,
            0
          );
          return [
            brand.brandName,
            brand.items.length,
            totalQty,
            parseFloat(totalSales.toFixed(2)),
          ];
        }),
        excelOptions: {
          columnWidths: [
            { width: 25 },
            { width: 15 },
            { width: 15 },
            { width: 20 },
          ],
          numberFormats: {
            1: "0",
            2: "0",
            3: "0.00",
          },
        },
      };
      sheets.unshift(summarySheet);
    }

    props.getData({
      type: "excel",
      isMultiSheet: true,
      title: "Sales by Revenue Center Items - All Brands",
      filter: getFilterData(),
      sheets: sheets,
    });
  };

  // Updated columns to display merged rows like item sales
  const columns = [
    {
      Header: "Business Name",
      id: "businessName",
      accessor: (data) => {
        return data.businessName[0]?.name || "Unknown Business";
      },
      Cell: (row) => {
        return <span>{row.value}</span>;
      },
      width: 200,
    },
    {
      Header: "Item No",
      accessor: "refNo",
      Cell: (row) => {
        const item = row.original;
        return <span>{item.refNo}</span>;
      },
      width: 100,
    },
    {
      Header: "Name",
      accessor: "name",
      Cell: (row) => {
        const item = row.original;
        return <span>{item.name}</span>;
      },
      width: 200,
    },
    {
      id: "currency",
      Header: "Currency",
      accessor: (data) => {
        return data.businessName[0]?.currency || "QAR";
      },
      Cell: (row) => {
        return <span>{row.value}</span>;
      },
      width: 80,
    },
    {
      Header: "Qty",
      accessor: "qty",
      Cell: (row) => {
        const item = row.original;
        const hasMultiplePrices =
          item.priceVariants && item.priceVariants.length > 1;

        if (hasMultiplePrices) {
          return (
            <div>
              {item.priceVariants.map((variant, index) => (
                <div
                  key={index}
                  style={{
                    padding: "2px 0",
                    borderBottom:
                      index < item.priceVariants.length - 1
                        ? "1px solid #dcdcdcff"
                        : "none",
                  }}
                >
                  {variant.qty}
                </div>
              ))}
            </div>
          );
        }
        return <span>{row.value}</span>;
      },
      width: 80,
    },

    {
      Header: "Price",
      accessor: "amount",
      Cell: (row) => {
        const item = row.original;
        const hasMultiplePrices =
          item.priceVariants && item.priceVariants.length > 1;

        if (hasMultiplePrices) {
          return (
            <div>
              {item.priceVariants.map((variant, index) => (
                <div
                  key={index}
                  style={{
                    padding: "2px 0",
                    borderBottom:
                      index < item.priceVariants.length - 1
                        ? "1px solid #dcdcdcff"
                        : "none",
                  }}
                >
                  {commaNumber(variant.price.toFixed(2))}
                </div>
              ))}
            </div>
          );
        }
        return <span>{commaNumber(row.value.toFixed(2))}</span>;
      },
      width: 100,
    },
    {
      id: "discount",
      Header: "Discount",
      accessor: (data) => {
        return data.totalDiscountAmount || 0;
      },
      Cell: (row) => {
        const item = row.original;
        const hasMultiplePrices =
          item.priceVariants && item.priceVariants.length > 1;

        if (hasMultiplePrices) {
          return (
            <div>
              {item.priceVariants.map((variant, index) => (
                <div
                  key={index}
                  style={{
                    padding: "2px 0",
                    borderBottom:
                      index < item.priceVariants.length - 1
                        ? "1px solid #dcdcdcff"
                        : "none",
                  }}
                >
                  {variant.totalDiscountAmount > 0
                    ? commaNumber(variant.totalDiscountAmount.toFixed(2))
                    : "-"}
                </div>
              ))}
            </div>
          );
        }

        const value = row.value;
        return (
          <span>{value > 0 ? `${commaNumber(value.toFixed(2))}` : "-"}</span>
        );
      },
      width: 100,
    },
    {
      Header: "Sales",
      accessor: "total",
      Cell: (row) => {
        const item = row.original;
        const hasMultiplePrices =
          item.priceVariants && item.priceVariants.length > 1;

        if (hasMultiplePrices) {
          return (
            <div>
              {item.priceVariants.map((variant, index) => (
                <div
                  key={index}
                  style={{
                    padding: "2px 0",
                    borderBottom:
                      index < item.priceVariants.length - 1
                        ? "1px solid #dcdcdcff"
                        : "none",
                  }}
                >
                  {commaNumber(variant.total.toFixed(2))}
                </div>
              ))}
            </div>
          );
        }
        return <span>{commaNumber(row.value.toFixed(2))}</span>;
      },
      width: 100,
    },
    {
      id: "revCenterBreakdown",
      Header: "Total Sales Per Revenue Center",
      accessor: (data) => {
        return data.revCenterBreakdown || [];
      },
      Cell: (row) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {row.value && row.value.length > 0 ? (
            row.value.map((revCenter, index) => (
              <div
                key={index}
                style={{ marginBottom: "2px", fontSize: "11px" }}
              >
                {`${revCenter.revCenterName} | Qty ${commaNumber(
                  revCenter.qty
                )} | ${commaNumber(revCenter.amount.toFixed(2))} ${
                  revCenter.currency
                }`}
              </div>
            ))
          ) : (
            <div style={{ fontSize: "12px", color: "#999" }}>
              No Revenue Center Data
            </div>
          )}
        </div>
      ),
      width: 350,
    },
  ];

  const renderBrandTabs = () => {
    const showAllBrandsTab = data.brandwiseData.length > 1;
    return (
      <div className="brand-tabs" style={{ marginBottom: "20px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {showAllBrandsTab && (
            <button
              className={`tab-button ${activeBrand === 0 ? "active" : ""}`}
              onClick={() => setActiveBrand(0)}
              style={{
                padding: "8px 16px",
                border: "1px solid #ddd",
                backgroundColor: activeBrand === 0 ? "#f68923ff" : "#f8f9fa",
                color: activeBrand === 0 ? "white" : "#333",
                cursor: "pointer",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            >
              All Brands
            </button>
          )}
          {data.brandwiseData.map((brand, index) => (
            <button
              key={brand.brandKey}
              className={`tab-button ${
                activeBrand === index + 1 ? "active" : ""
              }`}
              onClick={() => setActiveBrand(index + 1)}
              style={{
                padding: "8px 16px",
                border: "1px solid #ddd",
                backgroundColor:
                  activeBrand === index + 1 ? "#f68923ff" : "#f8f9fa",
                color: activeBrand === index + 1 ? "white" : "#333",
                cursor: "pointer",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            >
              {brand.brandName} ({brand.items.length})
            </button>
          ))}
        </div>
      </div>
    );
  };

  const getCurrentTableData = () => {
    if (data.brandwiseData.length === 1) {
      return data.brandwiseData[0].items;
    }
    if (activeBrand === 0) {
      return data.table;
    } else {
      return data.brandwiseData[activeBrand - 1]?.items || [];
    }
  };

  const getCurrentBrandName = () => {
    if (data.brandwiseData.length === 1) {
      return data.brandwiseData[0].brandName;
    }
    if (activeBrand === 0) {
      return "All Brands";
    } else {
      return data.brandwiseData[activeBrand - 1]?.brandName || "";
    }
  };

  return (
    <div>
      {load < 2 ? (
        <Loader />
      ) : (
        <div>
          {renderBrandTabs()}

          <div
            style={{
              marginBottom: "16px",
              fontWeight: "bold",
              fontSize: "18px",
            }}
          >
            {getCurrentBrandName()} - Items by Revenue Center Report (
            {getCurrentTableData().length} items)
          </div>

          <div
            style={{ marginBottom: "16px", fontSize: "14px", color: "#666" }}
          >
            {/* <em>
              Items with multiple prices show stacked values in Qty, Price,
              Discount, and Sales columns
            </em> */}
          </div>

          <ReactTable
            className="-striped -highlight"
            data={getCurrentTableData()}
            columns={columns}
            sortable
            filterable
            defaultPageSize={10}
            getTdProps={() => ({
              style: { textAlign: "center" },
            })}
            noDataText={`No items found for ${getCurrentBrandName()}`}
          />
        </div>
      )}
    </div>
  );
};

export default Slider4;
